import Cookies from "js-cookie";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { classes } from "typestyle";
import { paths } from "../../App";
import { ReactComponent as IconKey } from "../../assets/icons/icon_key.svg";
import { ReactComponent as Logo } from "../../assets/small-logo-black.svg";
import { ReactComponent as IconCross } from "../../assets/icons/icon_cross.svg";
import useOutsideClick from "../../hooks/useOutsideClick";
import { Locale, Organization } from "../../types";
import { Box } from "@hooplab/ui-components";
import NavigationMenu from "./navigationMenu";
import * as styles from "./styles";
import { GenericTextSkeleton } from "@hooplab/ui-components";
import { useCurrentLangContext } from "../../hooks/useCurrentLang";
import { getDomain } from "../../environment";

type HeaderProps = {
  title?: React.ReactNode;
  organization: Organization;
  showCountdown?: boolean;
};

const Header: React.FC<HeaderProps> = ({ title, organization }) => {
  const [menuOpen, setMenuOpen] = React.useState(false);

  const menuTogglerRef = React.useRef(null);

  const location = useLocation();
  const showOrganizationName =
    location.pathname !== paths.organization() && location.pathname !== paths.organization() + "/";

  const isLoggedIn = !!Cookies.get("u");

  const intl = useIntl();

  useOutsideClick(menuTogglerRef, () => setMenuOpen(false));

  const { provided } = useCurrentLangContext()!;
  const { currentLocale, filteredAvailableLocales, onChangeLanguage } = provided;

  const shouldShowLang = Object.keys(filteredAvailableLocales).length > 1;

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.brand}>
          <a
            href={`https://www.${getDomain()}`}
            aria-label={intl.formatMessage({
              id: "components.Header.link",
              defaultMessage: "Link to Hoopla home page",
            })}
          >
            <Logo />
          </a>
        </div>
        <div className={styles.titleWrapper}>
          {showOrganizationName && <a href={paths.organization()}>{title || "Hoopla"}</a>}
        </div>
        <div
          className={classes(
            styles.rightSide,
            showOrganizationName ? styles.rightSideEventPadding : styles.rightSidePadding,
          )}
        >
          <a
            href={`/organizer`}
            className={classes(
              styles.organizerLink,
              isLoggedIn && styles.loggedInOrganizerLink,
              showOrganizationName && styles.eventPageOrganizerLink,
            )}
          >
            <IconKey width="14" />
            <span className={styles.appHeaderButtonText}>
              {isLoggedIn ? (
                <FormattedMessage
                  id="components.Header.organizerPage"
                  defaultMessage="Organizer page"
                />
              ) : (
                <FormattedMessage id="components.Header.organizer" defaultMessage="For organizer" />
              )}
            </span>
          </a>

          {shouldShowLang && (
            <div
              ref={menuTogglerRef}
              style={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                position: "relative",
              }}
            >
              <button
                className={styles.hamburgerMenuToggler}
                onClick={() => setMenuOpen((open) => !open)}
              >
                <div className={classes(styles.flag, showOrganizationName && styles.eventPageFlag)}>
                  {currentLocale?.flag}
                </div>
                <span style={{ fontWeight: 500, fontSize: "0.875rem", lineHeight: 1.25 }}>
                  {currentLocale?.short}
                </span>
              </button>

              <Box
                additionalCls={classes(
                  styles.hamburgerMenu,
                  menuOpen && styles.hamburgerMenuOpen,
                  styles.languageMenu,
                )}
              >
                <div className={styles.languageHeader}>
                  <FormattedMessage
                    tagName={"span"}
                    id="components.Header.changeLanguage"
                    defaultMessage={"Choose language"}
                  />
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                    }}
                  >
                    <IconCross height="12" />
                  </button>
                </div>
                <div className={styles.hamburgerMenuInner}>
                  {Object.entries(filteredAvailableLocales).map(([k, l]) => (
                    <button
                      key={l.name}
                      className={styles.menuOuter}
                      onClick={() => {
                        onChangeLanguage(k as Locale);
                        setMenuOpen(false);
                      }}
                      style={{
                        fontWeight: l.name === currentLocale.name ? 500 : 300,
                      }}
                    >
                      <div className={styles.menuItem}>
                        <div
                          className={styles.flag}
                          style={{ opacity: l.name === currentLocale.name ? 0.7 : 0.5 }}
                        >
                          {l.flag}
                        </div>
                        <div>{l.text}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </Box>
            </div>
          )}
          {showOrganizationName && (
            <NavigationMenu isLoggedIn={isLoggedIn} organization={organization} />
          )}
        </div>
      </div>
    </div>
  );
};

export const HeaderSkeleton: React.FC = () => {
  const intl = useIntl();
  const location = useLocation();
  const showOrganizationName =
    location.pathname !== paths.organization() && location.pathname !== paths.organization() + "/";

  const isLoggedIn = !!Cookies.get("u");
  const { provided } = useCurrentLangContext()!;
  const { currentLocale, filteredAvailableLocales } = provided;

  const shouldShowLang = Object.keys(filteredAvailableLocales).length > 1;

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.brand}>
          <a
            href={`https://www.${getDomain()}`}
            aria-label={intl.formatMessage({
              id: "components.Header.link",
              defaultMessage: "Link to Hoopla home page",
            })}
          >
            <Logo />
          </a>
        </div>
        <div className={styles.titleWrapper}>
          {showOrganizationName && (
            <div style={{ width: "100px" }}>
              <GenericTextSkeleton />
            </div>
          )}
        </div>
        <div
          className={classes(
            styles.rightSide,
            showOrganizationName ? styles.rightSideEventPadding : styles.rightSidePadding,
          )}
        >
          <a
            href={`/organizer`}
            className={classes(
              styles.organizerLink,
              isLoggedIn && styles.loggedInOrganizerLink,
              showOrganizationName && styles.eventPageOrganizerLink,
            )}
          >
            <IconKey width="14" />
            <span className={styles.appHeaderButtonText}>
              {isLoggedIn ? (
                <FormattedMessage
                  id="components.Header.organizerPage"
                  defaultMessage="Organizer page"
                />
              ) : (
                <FormattedMessage id="components.Header.organizer" defaultMessage="For organizer" />
              )}
            </span>
          </a>

          {shouldShowLang && (
            <div
              style={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                position: "relative",
              }}
            >
              <button className={styles.hamburgerMenuToggler}>
                <div className={classes(styles.flag, showOrganizationName && styles.eventPageFlag)}>
                  {currentLocale?.flag}
                </div>
                <span style={{ fontWeight: 500, fontSize: "0.875rem", lineHeight: 1.25 }}>
                  {currentLocale?.short}
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
